import React from 'react';

/**
 * CommonLoader — a clean, professional spinner themed with the global light
 * tokens (src/styles/theme.css). Self-contained: injects its own CSS, no image
 * asset or react-bootstrap dependency, so it drops into any file unchanged.
 *
 * Three render modes (pick one):
 *   • inline (default)  — sits in the normal flow; use inside buttons, table
 *                         cells, next to a label, etc.
 *   • overlay           — absolutely fills the nearest positioned ancestor;
 *                         use to cover a card/panel/section while it loads
 *                         (the parent needs `position: relative`).
 *   • fullPage          — fixed overlay covering the whole viewport.
 *
 * Props:
 *   size     — spinner diameter in px (default 28)
 *   thickness— ring stroke width in px (default derives from size)
 *   label    — optional text shown beside (inline) or below (overlay/fullPage)
 *   variant  — 'primary' (navy, default) | 'inverse' (white, for dark surfaces)
 *   overlay  — render as a section overlay
 *   fullPage — render as a full-viewport overlay
 *   className, style — forwarded to the outermost element
 *
 * Examples:
 *   <CommonLoader />                                   // inline spinner
 *   <CommonLoader label="Loading risks…" />            // inline + text
 *   <CommonLoader overlay label="Saving…" />           // covers its card
 *   <CommonLoader fullPage label="Please wait…" />     // whole screen
 *   <CommonButton text={busy ? <CommonLoader size={16} variant="inverse" /> : 'Save'} />
 */
function CommonLoader({
    size = 28,
    thickness,
    label,
    variant = 'primary',
    overlay = false,
    fullPage = false,
    className = '',
    style,
}) {
    const stroke = thickness || Math.max(2, Math.round(size / 10));
    const spinner = (
        <span
            className={`cmn-loader__spinner cmn-loader__spinner--${variant}`}
            style={{ width: size, height: size, borderWidth: stroke }}
            role="status"
            aria-label={label || 'Loading'}
        />
    );

    // Inline: spinner (+ label) in normal flow.
    if (!overlay && !fullPage) {
        return (
            <span className={`cmn-loader cmn-loader--inline ${className}`} style={style}>
                <CommonLoaderStyles />
                {spinner}
                {label && <span className="cmn-loader__label">{label}</span>}
            </span>
        );
    }

    // Overlay (section) or fullPage (viewport): centred spinner + optional label.
    const mode = fullPage ? 'fullpage' : 'overlay';
    return (
        <div className={`cmn-loader cmn-loader--${mode} ${className}`} style={style} role="status" aria-live="polite">
            <CommonLoaderStyles />
            <div className="cmn-loader__content">
                {spinner}
                {label && <span className="cmn-loader__label">{label}</span>}
            </div>
        </div>
    );
}

// Styles are injected once by class-name presence; duplicate <style> tags with
// identical text are deduped by the browser's CSSOM, so rendering many loaders
// is cheap. Kept inside the component file so it's truly drop-in.
function CommonLoaderStyles() {
    return <style>{CMN_LOADER_CSS}</style>;
}

const CMN_LOADER_CSS = `
@keyframes cmn-loader-spin { to { transform: rotate(360deg); } }

.cmn-loader { font-family: var(--font-family); }

.cmn-loader--inline {
    display: inline-flex; align-items: center; gap: 10px; vertical-align: middle;
}

.cmn-loader__spinner {
    box-sizing: border-box; display: inline-block; border-radius: 50%;
    border-style: solid; animation: cmn-loader-spin 0.7s linear infinite;
    flex-shrink: 0;
}
.cmn-loader__spinner--primary {
    border-color: var(--color-border-strong, #cbd2dd);
    border-top-color: var(--color-primary, #1e3c65);
}
.cmn-loader__spinner--inverse {
    border-color: rgba(255, 255, 255, 0.35);
    border-top-color: var(--color-text-inverse, #ffffff);
}

.cmn-loader__label {
    font-size: 13px; font-weight: 500; color: var(--color-text-muted, #8A93A3);
    line-height: 1.4;
}

.cmn-loader__content {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.cmn-loader--overlay .cmn-loader__label,
.cmn-loader--fullpage .cmn-loader__label { text-align: center; }

.cmn-loader--overlay {
    position: absolute; inset: 0; z-index: 20;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255, 255, 255, 0.7);
    -webkit-backdrop-filter: blur(1px); backdrop-filter: blur(1px);
    border-radius: inherit;
}

.cmn-loader--fullpage {
    position: fixed; inset: 0; z-index: 2000;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255, 255, 255, 0.55);
    -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px);
}
`;

export default CommonLoader;
